import { Communication, Extension, Identifier } from '@medplum/fhirtypes';
import { getExtension } from '@medplum/core';
import { TWILIO_SYSTEM } from './constants';
import { formatPhoneNumber } from './phone-validations';
import { TWILIO_CONFERENCE_SESSION_EXTENSION } from './extensions';

export const MAX_CONFERENCE_PARTICIPANTS = 250;

export const ConferencePhase = {
  P2P: 'p2p',
  CONSULT: 'consult',
  ACTIVE: 'active',
} as const;

export type ConferencePhase = (typeof ConferencePhase)[keyof typeof ConferencePhase];

/** @deprecated Use ConferencePhase.ACTIVE — legacy persisted value `merged`. */
export const ConferencePhaseMergedLegacy = 'merged' as const;

export const ConferenceParticipantRole = {
  AGENT: 'agent',
  PRIMARY: 'primary',
  GUEST: 'guest',
} as const;

export type ConferenceParticipantRole = (typeof ConferenceParticipantRole)[keyof typeof ConferenceParticipantRole];

/** @deprecated Use ConferenceParticipantRole.GUEST — legacy persisted value `third`. */
export const ConferenceParticipantRoleThirdLegacy = 'third' as const;

export const ConferenceParticipantStatus = {
  DIALING: 'dialing',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
} as const;

export type ConferenceParticipantStatus =
  (typeof ConferenceParticipantStatus)[keyof typeof ConferenceParticipantStatus];

export const ConferenceControlAction = {
  START_ADD: 'startAdd',
  MERGE: 'merge',
  CANCEL_CONSULT: 'cancelConsult',
  DROP_PARTICIPANT: 'dropParticipant',
  GET_STATUS: 'getStatus',
  RECONNECT_PRIMARY: 'reconnectPrimary',
} as const;

export type ConferenceControlAction = (typeof ConferenceControlAction)[keyof typeof ConferenceControlAction];

export interface ConferenceParticipantRef {
  role: ConferenceParticipantRole;
  /** Omitted while guest outbound leg is being created (pre-dial). */
  callSid?: string;
  /** Twilio participant label — required for guests (`guest-1`, `guest-2`, …). */
  label?: string;
  providerReference?: string;
  phone?: string;
  /** Resolved when guest is added (practitioner name or formatted phone). */
  displayName?: string;
  status?: ConferenceParticipantStatus;
}

export type ConferenceParticipantDisplayContext = {
  /** Patient name for the primary participant when known in the UI. */
  patientDisplayName?: string;
  /** Logged-in practitioner reference (e.g. Practitioner/{id}) for viewer-relative "You". */
  currentProviderReference?: string;
  /** True when the viewer is a guest practitioner on this conference (not the host). */
  viewerIsGuest?: boolean;
};

export type ConferenceCallDirection = 'outbound' | 'inbound';

/**
 * Conference session persisted on the primary phone Communication.
 * `parentCallSid` matches `Communication.identifier` (TWILIO_SYSTEM parent leg).
 */
export interface TwilioConferenceSession {
  parentCallSid: string;
  direction: ConferenceCallDirection;
  conferenceFriendlyName: string;
  conferenceSid?: string;
  phase: ConferencePhase;
  participants: ConferenceParticipantRef[];
  agentCallSid: string;
  primaryCallSid: string;
  /** Guest on private consult line while phase is `consult`. */
  activeConsultCallSid?: string;
  /** Next guest label index (`guest-{n}`). */
  nextGuestIndex: number;
  /** Set when the patient (primary) leaves the conference while the call continues. */
  primaryDisconnectedAt?: string;
  /** Phase to restore when consult is cancelled or guest dial fails (usually ACTIVE or P2P). */
  consultReturnPhase?: ConferencePhase;
  updatedAt: string;
}

export type ConferenceControlInput = {
  action: ConferenceControlAction;
  /** Any registered conference call leg SID (parent, agent, primary, or guest). */
  parentCallSid: string;
  /** E.164 or 10-digit US for PSTN guest (startAdd). */
  toPhone?: string;
  /** Practitioner reference string, e.g. Practitioner/{id} — rings Voice SDK client (startAdd). */
  providerReference?: string;
  /** Guest Call SID to remove (dropParticipant). */
  participantCallSid?: string;
};

/** Provider-app payload for startConferenceAdd (phone or in-app provider). */
export type ConferenceStartAddTarget =
  | { type: 'phone'; toPhone: string }
  | { type: 'provider'; providerReference: string };

export type ConferenceControlResult = {
  session?: TwilioConferenceSession;
  error?: string;
  /** True when the caller is the host or a connected guest practitioner (may add, merge, or drop). */
  canManageConference?: boolean;
};

/** All Twilio call leg SIDs indexed on the Communication for conference lookup. */
export function collectConferenceCallLegSids(session: TwilioConferenceSession): string[] {
  const sids = new Set<string>();
  sids.add(session.parentCallSid);
  sids.add(session.agentCallSid);
  sids.add(session.primaryCallSid);
  if (session.activeConsultCallSid) {
    sids.add(session.activeConsultCallSid);
  }
  for (const participant of session.participants) {
    if (participant.callSid) {
      sids.add(participant.callSid);
    }
  }
  return [...sids];
}

export function mergeConferenceLegIdentifiers(
  communication: Communication,
  session: TwilioConferenceSession | undefined
): Identifier[] {
  const legSids = session ? collectConferenceCallLegSids(session) : [];
  const byValue = new Map((communication.identifier ?? []).map((id) => [id.value, id]));
  for (const sid of legSids) {
    if (!byValue.has(sid)) {
      byValue.set(sid, { system: TWILIO_SYSTEM, value: sid });
    }
  }
  return [...byValue.values()];
}

/** Add a single Twilio call leg SID to Communication.identifier without requiring a conference session. */
export function mergeTwilioCallLegIdentifier(communication: Communication, callLegSid: string): Identifier[] {
  const byValue = new Map((communication.identifier ?? []).map((id) => [id.value, id]));
  if (!byValue.has(callLegSid)) {
    byValue.set(callLegSid, { system: TWILIO_SYSTEM, value: callLegSid });
  }
  return [...byValue.values()];
}

export function buildGuestParticipantLabel(index: number): string {
  return `guest-${index}`;
}

export function isGuestParticipant(participant: ConferenceParticipantRef): boolean {
  return participant.role === ConferenceParticipantRole.GUEST;
}

export function getGuestParticipants(session: TwilioConferenceSession): ConferenceParticipantRef[] {
  return session.participants.filter(isGuestParticipant);
}

export function getActiveGuestParticipants(session: TwilioConferenceSession): ConferenceParticipantRef[] {
  return getGuestParticipants(session).filter((p) => p.status !== ConferenceParticipantStatus.DISCONNECTED);
}

function dedupeRemovableParticipants(participants: ConferenceParticipantRef[]): ConferenceParticipantRef[] {
  const byKey = new Map<string, ConferenceParticipantRef>();

  for (const participant of participants) {
    const key = participant.providerReference ?? participant.phone ?? participant.label ?? participant.callSid ?? '';
    if (!key) {
      continue;
    }
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, participant);
      continue;
    }
    const preferNew =
      participant.status === ConferenceParticipantStatus.CONNECTED &&
      existing.status !== ConferenceParticipantStatus.CONNECTED;
    const newIndex = participant.label ? Number.parseInt(participant.label.replace(/^guest-/, ''), 10) : 0;
    const oldIndex = existing.label ? Number.parseInt(existing.label.replace(/^guest-/, ''), 10) : 0;
    if (preferNew || newIndex > oldIndex) {
      byKey.set(key, participant);
    }
  }

  return [...byKey.values()];
}

/** Active guests deduped for Remove UI — one entry per provider or phone (latest connected wins). */
export function getRemovableGuestParticipants(session: TwilioConferenceSession): ConferenceParticipantRef[] {
  return dedupeRemovableParticipants(getActiveGuestParticipants(session));
}

/** Active agent + guest participants (excludes patient) deduped for Remove UI. */
export function getRemovableProviderParticipants(session: TwilioConferenceSession): ConferenceParticipantRef[] {
  const active = session.participants.filter(
    (p) =>
      p.status !== ConferenceParticipantStatus.DISCONNECTED &&
      (p.role === ConferenceParticipantRole.AGENT || isGuestParticipant(p))
  );
  return dedupeRemovableParticipants(active);
}

export function getPrimaryParticipant(session: TwilioConferenceSession): ConferenceParticipantRef | undefined {
  return session.participants.find((p) => p.role === ConferenceParticipantRole.PRIMARY);
}

export function isPrimaryParticipantConnected(session: TwilioConferenceSession): boolean {
  const primary = getPrimaryParticipant(session);
  return primary?.status !== ConferenceParticipantStatus.DISCONNECTED;
}

export type ConferenceParticipantLeaveParams = {
  callSid?: string;
  participantLabel?: string;
};

function participantMatchesLeaveTarget(
  participant: ConferenceParticipantRef,
  session: TwilioConferenceSession,
  params: ConferenceParticipantLeaveParams
): boolean {
  const { callSid, participantLabel } = params;
  if (callSid && participant.callSid === callSid) {
    return true;
  }
  if (participantLabel && participant.label === participantLabel) {
    return true;
  }
  if (callSid) {
    if (participant.role === ConferenceParticipantRole.AGENT && session.agentCallSid === callSid) {
      return true;
    }
    if (participant.role === ConferenceParticipantRole.PRIMARY && session.primaryCallSid === callSid) {
      return true;
    }
  }
  return false;
}

/** Mark a conference participant disconnected from a Twilio leave callback. */
export function applyConferenceParticipantLeaveToSession(
  session: TwilioConferenceSession,
  params: ConferenceParticipantLeaveParams
): TwilioConferenceSession | undefined {
  const { callSid, participantLabel } = params;
  if (!callSid && !participantLabel) {
    return undefined;
  }

  const leavingPrimary = (callSid && session.primaryCallSid === callSid) || participantLabel === 'primary';
  const leavingConsultGuest =
    session.phase === ConferencePhase.CONSULT && callSid != null && session.activeConsultCallSid === callSid;

  const hasMatch = session.participants.some((p) => participantMatchesLeaveTarget(p, session, params));
  if (!hasMatch && !leavingPrimary) {
    return undefined;
  }

  const now = new Date().toISOString();
  const remainingActiveGuests = getActiveGuestParticipants(session).filter(
    (p) => !participantMatchesLeaveTarget(p, session, params)
  );

  let nextPhase = session.phase;
  let activeConsultCallSid = session.activeConsultCallSid;
  let primaryDisconnectedAt = session.primaryDisconnectedAt;

  if (leavingConsultGuest) {
    activeConsultCallSid = remainingActiveGuests[0]?.callSid;
    if (remainingActiveGuests.length === 0) {
      nextPhase = session.consultReturnPhase ?? ConferencePhase.P2P;
      activeConsultCallSid = undefined;
    }
  } else if (session.phase === ConferencePhase.CONSULT && leavingPrimary) {
    nextPhase = ConferencePhase.P2P;
    activeConsultCallSid = undefined;
  }

  if (leavingPrimary) {
    primaryDisconnectedAt = now;
  }

  return {
    ...session,
    phase: nextPhase,
    activeConsultCallSid,
    primaryDisconnectedAt,
    participants: session.participants.map((p) => {
      if (!participantMatchesLeaveTarget(p, session, params)) {
        return p;
      }
      return { ...p, status: ConferenceParticipantStatus.DISCONNECTED };
    }),
    updatedAt: now,
  };
}

export function countConferenceParticipants(session: TwilioConferenceSession): number {
  const core = session.participants.filter(
    (p) =>
      p.role === ConferenceParticipantRole.AGENT ||
      p.role === ConferenceParticipantRole.PRIMARY ||
      (isGuestParticipant(p) && p.status !== ConferenceParticipantStatus.DISCONNECTED)
  );
  return core.length;
}

/** Revert a failed consult guest dial: drop the pending guest and restore a dialable phase. */
export function buildGuestDialRollbackSession(
  existing: TwilioConferenceSession,
  pendingGuestLabel: string,
  rollbackPhase: ConferencePhase
): TwilioConferenceSession {
  return {
    ...existing,
    phase: rollbackPhase,
    activeConsultCallSid: rollbackPhase === ConferencePhase.CONSULT ? existing.activeConsultCallSid : undefined,
    consultReturnPhase: rollbackPhase === ConferencePhase.CONSULT ? existing.consultReturnPhase : undefined,
    participants: existing.participants.filter(
      (participant) => !(isGuestParticipant(participant) && participant.label === pendingGuestLabel)
    ),
    updatedAt: new Date().toISOString(),
  };
}

export function canAddConferenceParticipant(session: TwilioConferenceSession | undefined): string | null {
  if (!session) {
    return null;
  }
  if (session.phase === ConferencePhase.CONSULT) {
    return 'Finish the current consult before adding another person';
  }
  if (countConferenceParticipants(session) >= MAX_CONFERENCE_PARTICIPANTS) {
    return 'Conference is full';
  }
  return null;
}

/** True when the host may merge the active private consult into the main conference. */
export function canMergeConsult(session: TwilioConferenceSession | undefined): boolean {
  if (!session || session.phase !== ConferencePhase.CONSULT || !session.activeConsultCallSid) {
    return false;
  }
  const consultGuest = session.participants.find(
    (p) => isGuestParticipant(p) && p.callSid === session.activeConsultCallSid
  );
  return consultGuest?.status === ConferenceParticipantStatus.CONNECTED;
}

/** True while the active consult guest is still being dialed (ringing, not yet connected). */
export function isConsultGuestDialing(session: TwilioConferenceSession | undefined): boolean {
  if (!session?.activeConsultCallSid) {
    return false;
  }
  const consultGuest = session.participants.find(
    (p) => isGuestParticipant(p) && p.callSid === session.activeConsultCallSid
  );
  return consultGuest?.status === ConferenceParticipantStatus.DIALING;
}

/** UI label for a conference participant role (e.g. call notification "On call" list). */
export function formatConferenceParticipantRoleLabel(role: ConferenceParticipantRole): string {
  switch (role) {
    case ConferenceParticipantRole.PRIMARY:
      return 'Patient';
    case ConferenceParticipantRole.AGENT:
      return 'You';
    case ConferenceParticipantRole.GUEST:
      return 'Guest';
  }
}

export function formatConferenceParticipantDisplayLabel(
  participant: ConferenceParticipantRef,
  context?: ConferenceParticipantDisplayContext
): string {
  const currentRef = context?.currentProviderReference;

  if (currentRef && participant.providerReference === currentRef) {
    return 'You';
  }

  if (participant.displayName) {
    return participant.displayName;
  }

  if (participant.role === ConferenceParticipantRole.PRIMARY && context?.patientDisplayName) {
    return context.patientDisplayName;
  }

  if (participant.role === ConferenceParticipantRole.GUEST) {
    if (participant.phone) {
      return formatPhoneNumber(participant.phone);
    }
    if (participant.label) {
      const suffix = participant.label.replace(/^guest-/, '');
      return suffix ? `Guest ${suffix}` : 'Guest';
    }
  }

  if (currentRef && participant.role === ConferenceParticipantRole.AGENT && !context?.viewerIsGuest) {
    return 'You';
  }

  return formatConferenceParticipantRoleLabel(participant.role);
}

function normalizeParticipantRole(role: string): ConferenceParticipantRole {
  if (role === ConferenceParticipantRoleThirdLegacy) {
    return ConferenceParticipantRole.GUEST;
  }
  return role as ConferenceParticipantRole;
}

function normalizePhase(phase: string): ConferencePhase {
  if (phase === ConferencePhaseMergedLegacy) {
    return ConferencePhase.ACTIVE;
  }
  return phase as ConferencePhase;
}

/** Migrate legacy single-third-party sessions to the multi-guest model. */
export function normalizeTwilioConferenceSession(raw: TwilioConferenceSession): TwilioConferenceSession {
  const phase = normalizePhase(raw.phase);
  let participants = raw.participants.map((p) => ({
    ...p,
    role: normalizeParticipantRole(p.role as string),
    label:
      p.label ?? (normalizeParticipantRole(p.role as string) === ConferenceParticipantRole.GUEST ? 'guest-1' : p.label),
    status: p.status ?? ConferenceParticipantStatus.CONNECTED,
  }));

  const legacyThird = (raw as { thirdPartyCallSid?: string }).thirdPartyCallSid;
  if (legacyThird && !participants.some((p) => p.callSid === legacyThird)) {
    participants = [
      ...participants,
      {
        role: ConferenceParticipantRole.GUEST,
        callSid: legacyThird,
        label: 'guest-1',
        status: ConferenceParticipantStatus.CONNECTED,
      },
    ];
  }

  const guestLabels = participants
    .filter(isGuestParticipant)
    .map((p) => p.label)
    .filter((l): l is string => Boolean(l));
  const maxGuestIndex = guestLabels.reduce((max, label) => {
    const match = /^guest-(\d+)$/.exec(label);
    return match ? Math.max(max, Number.parseInt(match[1], 10)) : max;
  }, 0);

  const activeConsultCallSid =
    raw.activeConsultCallSid ??
    (phase === ConferencePhase.CONSULT
      ? participants.find((p) => isGuestParticipant(p) && p.status === ConferenceParticipantStatus.DIALING)?.callSid
      : undefined);

  return {
    ...raw,
    phase,
    participants,
    activeConsultCallSid,
    nextGuestIndex: raw.nextGuestIndex ?? maxGuestIndex + 1,
  };
}

export function parseTwilioConferenceSession(communication: Communication): TwilioConferenceSession | undefined {
  const ext = getExtension(communication, TWILIO_CONFERENCE_SESSION_EXTENSION);
  const raw = ext?.valueString;
  if (!raw) {
    return undefined;
  }
  try {
    return normalizeTwilioConferenceSession(JSON.parse(raw) as TwilioConferenceSession);
  } catch {
    return undefined;
  }
}

export function buildConferenceSessionExtension(session: TwilioConferenceSession): Extension {
  return {
    url: TWILIO_CONFERENCE_SESSION_EXTENSION,
    valueString: JSON.stringify(session),
  };
}

/** Merge conference session into Communication.extension (replace existing session ext if any). */
export function setConferenceSessionOnCommunication(
  communication: Communication,
  session: TwilioConferenceSession
): Communication {
  const nextExt = [...(communication.extension ?? [])];
  const idx = nextExt.findIndex((e) => e.url === TWILIO_CONFERENCE_SESSION_EXTENSION);
  const ext = buildConferenceSessionExtension(session);
  if (idx >= 0) {
    nextExt[idx] = ext;
  } else {
    nextExt.push(ext);
  }
  return { ...communication, extension: nextExt };
}

/** Persist session extension and register all call leg SIDs on the Communication. */
export function withConferenceSessionAndLegIdentifiers(
  communication: Communication,
  session: TwilioConferenceSession
): Pick<Communication, 'extension' | 'identifier'> {
  const withSession = setConferenceSessionOnCommunication(communication, session);
  return {
    extension: withSession.extension,
    identifier: mergeConferenceLegIdentifiers(communication, session),
  };
}
